import { GestorDashboardTaskIdChip } from '@/components/Gestores/GestorDashboardTaskIdChip'
import { GestorDashboardMetricCard } from '@/components/Gestores/GestorDashboardWidgets'
import { formatDashboardDate, formatDashboardDateTime, formatEffortMinutes } from '@/components/Gestores/gestorDashboardFormat'
import type { GestorDashboardTaskDetail } from '@/components/Gestores/gestorDashboardTypes'
import { GestorListTableShell } from '@/components/Gestores/GestorListTableShell'

function InfoRow({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null
  return (
    <div className='rounded-lg border border-[var(--glass-border)] bg-[var(--glass-bg)] px-3 py-2'>
      <p className='text-[10px] font-semibold uppercase tracking-wide text-text-muted'>{label}</p>
      <p className='mt-1 text-sm text-text-primary'>{value}</p>
    </div>
  )
}

type Props = {
  detail: GestorDashboardTaskDetail
}

export function GestorDashboardTaskDetailContent({ detail }: Props) {
  return (
    <div className='space-y-4'>
      <div className='glass-card p-4 sm:p-5'>
        <div className='flex flex-wrap items-start justify-between gap-3'>
          <div className='min-w-0 space-y-2'>
            <div className='flex flex-wrap items-center gap-2'>
              <GestorDashboardTaskIdChip taskId={detail.taskId} />
              {detail.situationLabel ? (
                <span className='rounded-full bg-accent/10 px-2 py-0.5 text-xs font-medium text-accent'>
                  {detail.situationLabel}
                </span>
              ) : null}
              {detail.priorityLabel ? (
                <span className='rounded-full bg-white/[0.06] px-2 py-0.5 text-xs text-text-muted'>
                  {detail.priorityLabel}
                </span>
              ) : null}
            </div>
            <h2 className='text-lg font-semibold text-text-primary'>{detail.title}</h2>
            {detail.description ? <p className='text-sm text-text-secondary'>{detail.description}</p> : null}
            <p className='text-xs text-text-muted'>
              Fonte: {detail.source === 'ekyte' ? 'eKyte API' : detail.source === 'cache' ? 'sync do dashboard' : 'apontamentos do período'}
              {' · '}
              Copie o ID e busque em Controle de Tarefas no eKyte.
            </p>
          </div>
        </div>
      </div>

      <div className='grid gap-3 sm:grid-cols-2 xl:grid-cols-4'>
        <GestorDashboardMetricCard
          label='Esforço no período'
          value={formatEffortMinutes(detail.effortSummary.totalEffortMinutes)}
        />
        <GestorDashboardMetricCard
          label='Horas produtivas'
          value={formatEffortMinutes(detail.effortSummary.productiveEffortMinutes)}
          tone='success'
        />
        <GestorDashboardMetricCard label='Apontamentos' value={String(detail.effortSummary.entryCount)} />
        <GestorDashboardMetricCard
          label='Esforço eKyte (actualTime)'
          value={detail.actualTimeMinutes != null ? formatEffortMinutes(detail.actualTimeMinutes) : '—'}
        />
      </div>

      <div className='grid gap-3 md:grid-cols-2 xl:grid-cols-3'>
        <InfoRow label='Cliente' value={detail.workspace} />
        <InfoRow label='Executor' value={detail.executor} />
        <InfoRow label='Tipo de tarefa' value={detail.taskType} />
        <InfoRow label='Etapa atual' value={detail.phase} />
        <InfoRow label='Projeto' value={detail.project} />
        <InfoRow label='Ticket vinculado' value={detail.ticketId ? `#${detail.ticketId}` : null} />
        <InfoRow label='Criação' value={detail.creationDate ? formatDashboardDateTime(detail.creationDate) : null} />
        <InfoRow label='Entrega atual' value={detail.currentDueDate ? formatDashboardDateTime(detail.currentDueDate) : null} />
        <InfoRow label='Conclusão' value={detail.resolvedDate ? formatDashboardDateTime(detail.resolvedDate) : null} />
      </div>

      {detail.phases.length > 0 && (
        <div className='glass-card p-4 sm:p-5'>
          <h3 className='mb-3 text-sm font-semibold text-text-primary'>Etapas da tarefa</h3>
          <ul className='space-y-2'>
            {detail.phases.map((phase) => (
              <li key={phase.phase} className='flex items-center justify-between gap-3 text-sm'>
                <span className='text-text-primary'>{phase.phase}</span>
                <span className='tabular-nums text-text-muted'>
                  {phase.actualTimeMinutes != null ? formatEffortMinutes(phase.actualTimeMinutes) : '—'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {detail.comments.length > 0 && (
        <div className='glass-card p-4 sm:p-5'>
          <h3 className='mb-3 text-sm font-semibold text-text-primary'>Comentários recentes</h3>
          <ul className='space-y-3'>
            {detail.comments.map((comment, index) => (
              <li key={`${comment.createdAt ?? 'comment'}-${index}`} className='rounded-lg border border-[var(--glass-border)] px-3 py-2'>
                <p className='text-xs text-text-muted'>
                  {comment.author ?? 'Usuário'}
                  {comment.createdAt ? ` · ${formatDashboardDateTime(comment.createdAt)}` : ''}
                </p>
                <p className='mt-1 text-sm text-text-primary'>{comment.text}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className='space-y-2'>
        <h3 className='text-sm font-semibold text-text-primary'>Apontamentos no período</h3>
        <GestorListTableShell empty={detail.entries.length === 0} emptyTitle='Sem apontamentos para esta tarefa no período.'>
          <table className='min-w-full text-left text-sm'>
            <thead>
              <tr className='border-b border-[var(--glass-border)] text-xs uppercase tracking-wide text-text-muted'>
                <th className='px-3 py-2'>Data</th>
                <th className='px-3 py-2'>Esforço</th>
                <th className='px-3 py-2'>Executor</th>
                <th className='px-3 py-2'>Comentário</th>
                <th className='px-3 py-2'>Produtivo</th>
              </tr>
            </thead>
            <tbody>
              {detail.entries.map((entry) => (
                <tr key={entry.id} className='border-b border-[var(--glass-border)]'>
                  <td className='px-3 py-2 whitespace-nowrap'>{entry.date ? formatDashboardDate(entry.date) : '—'}</td>
                  <td className='px-3 py-2 tabular-nums'>{formatEffortMinutes(entry.effortMinutes)}</td>
                  <td className='px-3 py-2'>{entry.executor ?? '—'}</td>
                  <td className='max-w-[320px] truncate px-3 py-2'>{entry.titleOrComment ?? '—'}</td>
                  <td className='px-3 py-2'>{entry.productive ? 'Sim' : 'Não'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </GestorListTableShell>
      </div>
    </div>
  )
}
